import PropertiesEditor from '/src/components/propertiesEditor';
import orgApi from '@api/orgApi';
import ItemEditor from '@components/itemEditor';
import OrgUserList from '@components/orgUserList';
import useAuth from '@hooks/useAuth';
import { t } from '@i18n';
import { Spoiler, Stack, TextInput } from '@mantine/core';
import type { Org } from '@models/org';
import AlertList from '@routes/profile/orgs/-alertList';
import GroupList from '@routes/profile/orgs/-groupList';
import RoleList from '@routes/profile/orgs/-roleList';
import { createFileRoute } from '@tanstack/react-router';
import { createForm, LoadingBoundary } from 'cross-state/react';

export const Route = createFileRoute('/profile/orgs/$id')({
  component: OrgEditor,
});

const orgForm = createForm<Org>({
  defaultValue: {
    id: '',
    type: 'tenant',
    name: '',
  },
  validations: {
    type: { required: (x) => !!x },
    name: { required: (x) => !!x },
  },
});

function OrgEditor() {
  const { id: orgId } = Route.useParams();
  const { hasClaim } = useAuth();
  const isNew = orgId === 'new';

  // Creating and deleting organizations, naming them and pointing them at a billing account are global
  // admin territory. An organization's own admins maintain its external reference and properties.
  const canReadGlobally = hasClaim('orgs/read', 'global');
  const canWriteGlobally = hasClaim(isNew ? 'orgs/create' : 'orgs/write', 'global');
  const canWriteInOrg = !isNew && hasClaim('orgs/write', orgId);
  const readonly = orgId === 'global' || !(canWriteGlobally || canWriteInOrg);

  return (
    <ItemEditor
      domain="orgs"
      api={orgApi}
      readonly={readonly}
      deletable={hasClaim('orgs/delete', 'global') && orgId !== 'global'}
      form={orgForm}
      name={(org) => org.name}
      footer={
        <div
          css={{
            marginTop: 'var(--mantine-spacing-xl)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--mantine-spacing-md)',
          }}
        >
          {!isNew && hasClaim('roles/read', orgId) && <RoleList orgId={orgId} />}

          {!isNew && hasClaim('roles/read', orgId) && <AlertList orgId={orgId} />}

          <LoadingBoundary>{!isNew && <GroupList orgId={orgId} />}</LoadingBoundary>

          <LoadingBoundary>{!isNew && <OrgUserList type="org" orgId={orgId} />}</LoadingBoundary>
        </div>
      }
    >
      <orgForm.Field
        name="name"
        render={(props) => (
          <TextInput
            {...props}
            value={props.value ?? ''}
            label={t('orgs.properties.name.label')}
            withAsterisk
            readOnly={!canWriteGlobally}
            autoComplete="off"
            spellCheck={false}
          />
        )}
      />

      {canReadGlobally && (
        <orgForm.Field
          name="accountReference"
          render={(props) => (
            <TextInput
              {...props}
              value={props.value ?? ''}
              label={t('orgs.properties.accountReference.label')}
              description={t('orgs.properties.accountReference.description')}
              readOnly={!canWriteGlobally}
              autoComplete="off"
              spellCheck={false}
            />
          )}
        />
      )}

      <Spoiler
        showLabel={<>+ {t('external.show')}</>}
        hideLabel={<>- {t('external.hide')}</>}
        maxHeight={0}
        mt="md"
        css={{
          '--mantine-color-anchor': 'currentColor',
        }}
      >
        <Stack mb="md">
          <orgForm.Field
            name="externalReference"
            render={(props) => (
              <TextInput
                {...props}
                value={props.value ?? ''}
                label={t('external.externalReference.label')}
                description={t('external.externalReference.description')}
                autoComplete="off"
                spellCheck={false}
              />
            )}
          />

          <orgForm.Field name="externalProperties" render={(props) => <PropertiesEditor {...props} />} />
        </Stack>
      </Spoiler>
    </ItemEditor>
  );
}
